import React, { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * Mobile-first bottom drawer. Slides up from the bottom edge with a
 * dim+blur backdrop, drag handle, optional title row with close
 * button, scrollable body, and an optional pinned footer.
 *
 * Used as a shared primitive by every "panel that opens from the
 * navbar" — replaces the older top-anchored dropdowns which were
 * cramped on small phones and overlapped the live camera feed badly.
 *
 * Z-index: panel sits at z-[300] and its backdrop at z-[290], above
 * navbar (z-[200]) and any screen-level controls. See the policy
 * comment in global.css.
 *
 * Two visual tones supported:
 *   - "yellow" (default): yellow panel + black ink. Best for picker-
 *     like content (lists, search). Reads against the camera feed.
 *   - "dark": black panel + yellow ink. Best for dense form content
 *     (toggles, radio groups, sliders) that needs higher contrast.
 *
 * Closes on: Escape, backdrop tap, "X" button. Locks body scroll
 * while open so the page behind doesn't drift.
 */

export type DrawerTone = 'yellow' | 'dark';

interface BottomDrawerProps {
    open: boolean;
    onClose: () => void;
    title?: string;
    /** Pinned bottom slot, sits below the scrollable body. Use for
     *  primary actions ("New project", "Save", …). */
    footer?: ReactNode;
    children: ReactNode;
    /** Caps the panel height. Defaults to 75dvh, leaving the upper
     *  quarter of the screen visible so the user keeps context. */
    maxHeight?: string;
    /** When true, the X button isn't rendered. The handle, backdrop
     *  tap and ESC still close. */
    hideCloseButton?: boolean;
    /** Visual tone. Defaults to "yellow". */
    tone?: DrawerTone;
    /** Override the panel chrome (background, border). Escape hatch
     *  for one-off drawers that don't fit either tone. Tone-based
     *  defaults are used when this is omitted. */
    panelClassName?: string;
    /** Optional label for screen readers when no `title` is set. */
    ariaLabel?: string;
}

/**
 * Per-tone chrome shape. Every field is a class string except
 * `panelShadow`, which is a CSS box-shadow value applied inline.
 */
interface ToneChrome {
    /** Background gradient + radius. No border — see `panelShadow`. */
    panel: string;
    /**
     * Multi-layer box-shadow string (passed via inline style).
     * Layers, in cascade order:
     *   1. inset white/yellow highlight → bevel
     *   2. 1px outer line → hairline rule
     *   3. wide soft drop shadow → depth
     *   4. tight dark shadow → contact / sharpness
     */
    panelShadow: string;
    /** Drag-handle pill color. */
    handle: string;
    /** Hairline below the title row. */
    headerBorder: string;
    /** Ink color for the title. */
    title: string;
    /** Ink + state colors for the close button. */
    close: string;
}

const TONES: Record<DrawerTone, ToneChrome> = {
    yellow: {
        panel: 'bg-gradient-to-b from-yellow-400 to-yellow-400/[0.97] rounded-t-2xl',
        panelShadow: [
            'inset 0 1px 0 rgba(255, 255, 255, 0.28)',
            '0 -1px 0 rgba(180, 140, 20, 0.85)',
            '0 -10px 32px rgba(0, 0, 0, 0.45)',
            '0 -2px 8px rgba(0, 0, 0, 0.35)',
        ].join(', '),
        handle: 'bg-black/35',
        headerBorder: 'border-b border-black/10',
        title: 'text-black',
        close: 'bg-black/10 hover:bg-black/15 active:bg-black/25 text-black',
    },
    dark: {
        panel: 'bg-gradient-to-b from-neutral-950 to-black rounded-t-2xl',
        panelShadow: [
            'inset 0 1px 0 rgba(250, 204, 21, 0.14)',
            '0 -1px 0 rgba(250, 204, 21, 0.55)',
            '0 -10px 32px rgba(0, 0, 0, 0.7)',
            '0 -2px 8px rgba(0, 0, 0, 0.55)',
        ].join(', '),
        handle: 'bg-yellow-400/35',
        headerBorder: 'border-b border-yellow-400/15',
        title: 'text-yellow-300',
        close: 'bg-yellow-400/10 hover:bg-yellow-400/15 active:bg-yellow-400/25 text-yellow-300',
    },
};

const BottomDrawer: React.FC<BottomDrawerProps> = ({
    open,
    onClose,
    title,
    footer,
    children,
    maxHeight = '75dvh',
    hideCloseButton = false,
    tone = 'yellow',
    panelClassName,
    ariaLabel,
}) => {
    const t = TONES[tone];

    // ESC closes the drawer.
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    // Prevent the page behind from scrolling while the drawer is open.
    // Restores the previous overflow on close/unmount.
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    if (!open) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-[300] flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-label={title ?? ariaLabel}
        >
            <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                className="pincam-drawer-backdrop absolute inset-0 z-[290] bg-black/60 backdrop-blur-sm cursor-default"
            />

            <div
                className={`pincam-drawer-panel relative mt-auto z-[300] flex flex-col w-full sm:max-w-xl sm:mx-auto ${panelClassName ?? t.panel}`}
                style={{
                    maxHeight,
                    paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
                    paddingLeft: 'max(16px, env(safe-area-inset-left))',
                    paddingRight: 'max(16px, env(safe-area-inset-right))',
                    boxShadow: t.panelShadow,
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Drag handle — short, thin, low-contrast: reads as
                    an affordance without screaming. */}
                <div className="flex justify-center pt-2.5 pb-1 flex-shrink-0">
                    <div className={`w-9 h-1 rounded-full ${t.handle}`} aria-hidden="true" />
                </div>

                {title !== undefined && (
                    <div
                        className={`px-1 pt-2 pb-3 mb-1 flex items-center justify-between gap-2 flex-shrink-0 ${t.headerBorder}`}
                    >
                        <h2
                            className={`${t.title} font-bold leading-none`}
                            style={{
                                fontSize: 'clamp(17px, 4.2vw, 21px)',
                                fontFamily: "'Source Sans Pro', sans-serif",
                                letterSpacing: '-0.005em',
                            }}
                        >
                            {title}
                        </h2>
                        {!hideCloseButton && (
                            <button
                                type="button"
                                onClick={onClose}
                                className={`flex items-center justify-center rounded-full transition-colors flex-shrink-0 ${t.close}`}
                                style={{
                                    // Smaller than the standard --touch-target.
                                    // The handle + backdrop + ESC are also
                                    // affordances to close, so X doesn't need
                                    // a primary-action footprint. Stays
                                    // pleasantly tappable at 36-40 px.
                                    width: 'clamp(34px, 9vw, 38px)',
                                    height: 'clamp(34px, 9vw, 38px)',
                                }}
                                aria-label="Close"
                            >
                                <X size={16} />
                            </button>
                        )}
                    </div>
                )}

                <div className="flex-1 overflow-y-auto -mx-1 px-1">{children}</div>

                {footer && <div className="pt-3 flex-shrink-0">{footer}</div>}
            </div>
        </div>,
        document.body,
    );
};

export default BottomDrawer;
